import {
  MAX_EVENTS_PER_BATCH,
  sendEventsKeepalive,
  trackEvents,
} from '../api/analyticsApi'
import type { AnalyticsEvent } from '../types'

/**
 * Копилка событий аналитики.
 *
 * Слать каждое событие отдельным запросом нельзя — это выжигает лимит и
 * батарею. Копить долго тоже нельзя: временем события сервер считает момент
 * приёма, и через несколько минут время начинает заметно врать. Отсюда три
 * условия отправки ниже.
 */

/** Набралось столько — отправляем. С запасом до лимита в 50 на запрос. */
const FLUSH_SIZE = 20

/** Прошло столько с первого события в буфере — отправляем. */
const FLUSH_INTERVAL_MS = 30_000

/**
 * Потолок буфера. При переполнении выбрасываем самые старые: потерять хвост
 * лучше, чем съесть память, если сеть лежит долго.
 */
const MAX_BUFFERED = 200

/** Сколько раз повторяем отправку при сетевой ошибке. */
const MAX_RETRIES = 3

/** Пауза перед повтором растёт линейно: 1с, 2с, 3с. */
const RETRY_DELAY_MS = 1000

let buffer: AnalyticsEvent[] = []
let timer: ReturnType<typeof setTimeout> | null = null

const clearTimer = () => {
  if (timer !== null) {
    clearTimeout(timer)
    timer = null
  }
}

/** Битый контракт, а не сбой сети: повторять бессмысленно. */
const isContractError = (error: unknown): boolean =>
  (error as { response?: { status?: number } })?.response?.status === 422

const send = (events: AnalyticsEvent[], attempt = 1): void => {
  trackEvents(events).catch(error => {
    if (isContractError(error)) {
      console.warn('Аналитика: событие отклонено по контракту', error)
      return
    }
    if (attempt >= MAX_RETRIES) return
    setTimeout(() => send(events, attempt + 1), RETRY_DELAY_MS * attempt)
  })
}

/**
 * Отправить всё накопленное.
 *
 * События снимаем из буфера ДО запроса, а не после ответа: иначе всё, что
 * случится во время запроса, уедет вторым разом вместе с уже отправленным.
 */
export const flushEvents = (): void => {
  clearTimer()
  if (buffer.length === 0) return

  const pending = buffer
  buffer = []

  for (let i = 0; i < pending.length; i += MAX_EVENTS_PER_BATCH) {
    send(pending.slice(i, i + MAX_EVENTS_PER_BATCH))
  }
}

/**
 * Положить событие в буфер.
 *
 * Агрегировать на клиенте нельзя: сервер ждёт отдельную строку на каждое
 * событие — из сырых событий считаются и воронка, и CTR, и разрезы, из
 * счётчиков не считается ничего.
 */
export const bufferEvent = (event: AnalyticsEvent): void => {
  buffer.push(event)

  if (buffer.length > MAX_BUFFERED) {
    buffer = buffer.slice(buffer.length - MAX_BUFFERED)
  }

  if (buffer.length >= FLUSH_SIZE) {
    flushEvents()
    return
  }

  // Таймер заводим от ПЕРВОГО события в буфере и не перезапускаем следующими,
  // иначе поток событий мог бы откладывать отправку бесконечно.
  if (timer === null) {
    timer = setTimeout(flushEvents, FLUSH_INTERVAL_MS)
  }
}

/**
 * Отправить событие немедленно, мимо буфера.
 *
 * Для действий, которые уводят пользователя со страницы: накопленный буфер в
 * этот момент рискует не уехать вообще.
 */
export const sendEventNow = (event: AnalyticsEvent): void => {
  send([event])
}

/**
 * Отправлять накопленное, когда приложение уходит в фон или закрывается.
 *
 * Без этого хвост сессии теряется целиком: вкладку закрыли — буфер уехал в
 * никуда. `pagehide` ловит закрытие и переход по ссылке, `visibilitychange` —
 * сворачивание приложения, единственное надёжное событие в мобильных
 * браузерах и в Telegram.
 */
export const flushEventsKeepalive = (): void => {
  clearTimer()
  if (buffer.length === 0) return

  const pending = buffer
  buffer = []
  sendEventsKeepalive(pending)
}

export const installAnalyticsFlush = (): void => {
  if (typeof document === 'undefined') return

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushEventsKeepalive()
  })
  window.addEventListener('pagehide', flushEventsKeepalive)
}
